import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hoyEnChile } from '../utils/dateHelpers';
import { User, Users as UsersIcon, DollarSign, AlertCircle } from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { MiembroSearchResult } from '../services/miembroService';
import { movimientoService } from '../services/movimientoService';
import { ComboboxLazy, ComboboxLazyOption } from './ui/ComboboxLazy';
import { useMiembroSearch } from '../hooks/useMiembroSearch';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { ModalHeader } from './ui/ModalHeader';
import { useToast } from './ui/Toast';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import type { RespaldoMovimiento } from '../types/comprobante';

const AYUDA_COMPROBANTE_PA = 'El comprobante del pago automático que te envió el miembro.';
const MENSAJE_FALTANTE_PA = 'Adjunta el comprobante del pago automático que te envió el miembro.';

const ayudaDelComprobante = (esModoPA: boolean) => (esModoPA ? AYUDA_COMPROBANTE_PA : undefined);

/** El pago automático exige el comprobante del miembro en el acto y no lleva medio de pago. */
function opcionesComprobanteAporte(
  lumisial: { readonly id: string; readonly nombre: string } | null,
  fecha: string,
  esModoPA: boolean,
) {
  return {
    entidad: lumisial ? { tipo: 'lumisial' as const, id: lumisial.id, nombre: lumisial.nombre } : null,
    fechaMovimiento: fecha,
    permiteSubirDespues: !esModoPA,
    pideMedioPago: !esModoPA,
    sentidoDinero: 'ingreso' as const,
    mensajeFaltante: esModoPA ? MENSAJE_FALTANTE_PA : undefined,
  };
}

interface AporteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Cada apertura monta un formulario nuevo con el día civil actual de Chile.
export const AporteModal: React.FC<AporteModalProps> = (props) =>
  props.isOpen ? <FormularioAporte {...props} /> : null;

const FormularioAporte: React.FC<AporteModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Hook optimizado para búsqueda de miembros
  const { handleSearch: handleSearchMiembros } = useMiembroSearch(selectedLumisial?.id || null);
  
  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const [origenTipo, setOrigenTipo] = useState<'miembro' | 'externo'>('miembro');
  const [miembroId, setMiembroId] = useState('');
  const [miembroSeleccionado, setMiembroSeleccionado] = useState<MiembroSearchResult | null>(null);
  const [nombreExterno, setNombreExterno] = useState('');
  const [categoriaAporte, setCategoriaAporte] = useState<'aporte_mensual' | 'cuota_iglesia' | 'ambos'>('aporte_mensual');
  const [monto, setMonto] = useState('');
  const [montoAporteLumisial, setMontoAporteLumisial] = useState('');
  const [montoCuotaIglesia, setMontoCuotaIglesia] = useState('');
  const [fecha, setFecha] = useState(() => hoyEnChile());
  const [descripcion, setDescripcion] = useState('');

  const handleMiembroChange = useCallback((value: string, option?: ComboboxLazyOption) => {
    setMiembroId(value);
    if (option?.data) {
      setMiembroSeleccionado(option.data as MiembroSearchResult);
    } else {
      setMiembroSeleccionado(null);
    }
  }, []);

  useEffect(() => {
    if (miembroSeleccionado && origenTipo === 'miembro') {
      if (categoriaAporte === 'aporte_mensual') {
        setMonto(String(miembroSeleccionado.aporte_mensual || 0));
      } else if (categoriaAporte === 'cuota_iglesia') {
        setMonto(String(miembroSeleccionado.aporte_cuota_iglesia || 0));
      } else if (categoriaAporte === 'ambos') {
        setMontoAporteLumisial(String(miembroSeleccionado.aporte_mensual || 0));
        setMontoCuotaIglesia(String(miembroSeleccionado.aporte_cuota_iglesia || 0));
      }
    }
  }, [miembroSeleccionado, categoriaAporte, origenTipo]);

  // Detectar modo PA: miembro con pago automático seleccionando cuota iglesia
  const esModoPA = origenTipo === 'miembro'
    && categoriaAporte === 'cuota_iglesia'
    && !!miembroSeleccionado?.modo_pago_cuota_iglesia?.includes('PA');

  const submitLabel = esModoPA ? 'Confirmar PA' : 'Registrar Aporte';

  const comprobante = useComprobanteFormulario(opcionesComprobanteAporte(selectedLumisial, fecha, esModoPA));

  const validateAporte = (): string | null => {
    if (origenTipo === 'miembro' && !miembroId) return 'Por favor selecciona un miembro';
    if (origenTipo === 'externo' && !nombreExterno.trim()) return 'Por favor ingresa el nombre de la persona externa';
    if (origenTipo === 'miembro' && !categoriaAporte) return 'Por favor selecciona una categoría de aporte';
    if (origenTipo === 'miembro' && categoriaAporte === 'ambos') {
      if (!montoAporteLumisial || Number.parseFloat(montoAporteLumisial) <= 0) return 'Por favor ingresa un monto válido para el Aporte Lumisial';
      if (!montoCuotaIglesia || Number.parseFloat(montoCuotaIglesia) <= 0) return 'Por favor ingresa un monto válido para la Cuota Iglesia';
    } else if (!monto || Number.parseFloat(monto) <= 0) {
      return 'Por favor ingresa un monto válido';
    }
    return null;
  };

  const submitAmbos = async (lumisialId: string, respaldo: RespaldoMovimiento) => {
    await movimientoService.crearAporteDoble({
      lumisialId, miembroId, fecha, descripcion, respaldo,
      montoAporte: Number.parseFloat(montoAporteLumisial),
      montoCuota: Number.parseFloat(montoCuotaIglesia),
    });
    toast.success('Se registraron exitosamente 2 aportes');
  };

  const registrarAporte = async (lumisialId: string, respaldo: RespaldoMovimiento) => {
    if (esModoPA && miembroSeleccionado) {
      await movimientoService.confirmarPagoAutomaticoPA({
        miembroId,
        lumisialId,
        monto: Number.parseFloat(monto),
        fecha,
        modoPago: miembroSeleccionado.modo_pago_cuota_iglesia ?? 'Mensual con PA',
        miembroNombre: `${miembroSeleccionado.nombres} ${miembroSeleccionado.apellidos}`,
        usuarioId: usuario?.user_id ?? '',
        respaldo,
      });
    } else if (origenTipo === 'miembro' && categoriaAporte === 'ambos') {
      await submitAmbos(lumisialId, respaldo);
    } else {
      await movimientoService.create({
        fecha, tipo: 'ingreso', monto: Number.parseFloat(monto),
        descripcion: descripcion.trim() || 'Aporte',
        lumisial_id: lumisialId,
        origen_tipo: origenTipo,
        origen_id: origenTipo === 'miembro' ? miembroId : undefined,
        nombre_externo: origenTipo === 'externo' ? nombreExterno : undefined,
        categoria_aporte: origenTipo === 'miembro' && categoriaAporte !== 'ambos' ? categoriaAporte : undefined,
        requiere_reembolso: false, reembolsado: false, anulado: false, afecta_balance: true,
        ...respaldo
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!selectedLumisial) return;

    const validationError = validateAporte();
    if (validationError) { handleError(validationError); return; }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { handleError(errorComprobante); return; }

    try {
      setSubmitting(true);
      const respaldo = await comprobante.resolver();

      await registrarAporte(selectedLumisial.id, respaldo);

      resetForm();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al registrar el aporte. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setOrigenTipo('miembro');
    setMiembroId('');
    setNombreExterno('');
    setCategoriaAporte('aporte_mensual');
    setMonto('');
    setMontoAporteLumisial('');
    setMontoCuotaIglesia('');
    setFecha(hoyEnChile());
    setDescripcion('');
    comprobante.reiniciar();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const hasFormChanges = () => {
    return (
      origenTipo !== 'miembro' ||
      miembroId !== '' ||
      nombreExterno !== '' ||
      monto !== '' ||
      montoAporteLumisial !== '' ||
      montoCuotaIglesia !== '' ||
      fecha !== hoyEnChile() ||
      descripcion !== '' ||
      comprobante.hayCambios
    );
  };

  const hasUnsavedChanges = submitting || hasFormChanges();
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <ModalHeader
          title="Registrar Aporte"
          icon={<DollarSign className="w-6 h-6 text-emerald-600" />}
          onClose={requestClose}
        />
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormErrorAlert error={error} onClear={clearError} />

          <ComprobanteInput
            comprobante={comprobante}
            deshabilitado={submitting}
            ayuda={ayudaDelComprobante(esModoPA)}
          />

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Tipo de Aportante
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setOrigenTipo('miembro')}
                className={`
                  flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all
                  ${origenTipo === 'miembro'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }
                `}
              >
                <UsersIcon className="w-5 h-5" />
                <span className="font-medium">Miembro</span>
              </button>
              <button
                type="button"
                onClick={() => setOrigenTipo('externo')}
                className={`
                  flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all
                  ${origenTipo === 'externo'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }
                `}
              >
                <User className="w-5 h-5" />
                <span className="font-medium">Externo</span>
              </button>
            </div>
          </div>

          {origenTipo === 'miembro' ? (
            <>
              <div>
                <ComboboxLazy
                  label="Seleccionar Miembro"
                  value={miembroId}
                  onChange={handleMiembroChange}
                  onSearch={handleSearchMiembros}
                  placeholder="Buscar miembro por nombre o RUT..."
                  emptyMessage="No se encontraron miembros"
                  required
                  allowClear
                />
              </div>

              {miembroId && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Categoría de Aporte *
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setCategoriaAporte('aporte_mensual')}
                      className={`
                        p-4 rounded-xl border-2 transition-all text-left
                        ${categoriaAporte === 'aporte_mensual'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                    >
                      <div className="font-semibold text-slate-800 mb-1">Aporte Lumisial</div>
                      {miembroSeleccionado && miembroSeleccionado.aporte_mensual && (
                        <div className="text-sm font-medium text-blue-600 mt-2">
                          Compromiso: ${miembroSeleccionado.aporte_mensual.toLocaleString('es-CL')}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoriaAporte('cuota_iglesia')}
                      className={`
                        p-4 rounded-xl border-2 transition-all text-left
                        ${categoriaAporte === 'cuota_iglesia'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                    >
                      <div className="font-semibold text-slate-800 mb-1">Cuota Iglesia</div>
                      {miembroSeleccionado && miembroSeleccionado.aporte_cuota_iglesia && (
                        <div className="text-sm font-medium text-amber-600 mt-2">
                          Compromiso: ${miembroSeleccionado.aporte_cuota_iglesia.toLocaleString('es-CL')}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoriaAporte('ambos')}
                      className={`
                        p-4 rounded-xl border-2 transition-all text-left
                        ${categoriaAporte === 'ambos'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                    >
                      <div className="font-semibold text-slate-800 mb-1">Ambos Aportes</div>
                      {miembroSeleccionado && (
                        <div className="text-sm font-medium text-emerald-600 mt-2">
                          Total: ${((miembroSeleccionado.aporte_mensual || 0) + (miembroSeleccionado.aporte_cuota_iglesia || 0)).toLocaleString('es-CL')}
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <TextInput
              label="Nombre del Aportante"
              value={nombreExterno}
              onChange={setNombreExterno}
              placeholder="Nombre completo de la persona externa"
              required
            />
          )}

          {origenTipo === 'miembro' && categoriaAporte === 'ambos' ? (
            <>
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr 1.1fr' }}>
                <div>
                  <label htmlFor="montoAporteLumisial" className="block text-sm font-medium text-slate-600 mb-1.5">
                    Aporte Lumisial *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">$</span>
                    <input
                      type="number"
                      id="montoAporteLumisial"
                      value={montoAporteLumisial}
                      onChange={(e) => setMontoAporteLumisial(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="1"
                      className={`w-full pl-8 pr-4 py-3 border border-blue-300 hover:border-blue-400 ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="montoCuotaIglesia" className="block text-sm font-medium text-slate-600 mb-1.5">
                    Cuota Iglesia *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">$</span>
                    <input
                      type="number"
                      id="montoCuotaIglesia"
                      value={montoCuotaIglesia}
                      onChange={(e) => setMontoCuotaIglesia(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="1"
                      className={`w-full pl-8 pr-4 py-3 border border-amber-300 hover:border-amber-400 ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                      required
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <label htmlFor="fecha-ambos" className="block text-sm font-medium text-slate-600 mb-1.5">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    id="fecha-ambos"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                    required
                  />
                </div>
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-emerald-800">Suma Total</span>
                  <span className="text-2xl font-bold text-emerald-700">
                    ${((Number.parseFloat(montoAporteLumisial) || 0) + (Number.parseFloat(montoCuotaIglesia) || 0)).toLocaleString('es-CL')}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              {esModoPA && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p>
                    Este miembro paga cuota iglesia por pago automático directo a JAN.
                    A continuación puedes confirmar el pago siempre que hayas revisado el voucher del pago automático del mes o año correspondiente.
                  </p>
                </div>
              )}
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                label="Monto"
                type="number"
                value={monto}
                onChange={setMonto}
                placeholder="0"
                min={0}
                step={1}
                prefixIcon={<span className="text-slate-500 font-medium text-sm">$</span>}
                required
              />
              <TextInput
                label="Fecha"
                type="date"
                value={fecha}
                onChange={setFecha}
                required
              />
            </div>
            </>
          )}

          <MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />

          <Textarea
            label="Descripción (opcional)"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Agrega detalles sobre este aporte..."
            rows={3}
          />

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting || comprobante.subiendo}
            >
              {submitting ? 'Guardando...' : submitLabel}
            </button>
          </div>
        </form>
    </DialogShell>
  );
};
